/**
 * <ProtectedRoute />
 *
 * Wrap any route element in this to require authentication. While the
 * initial `/auth/me` check is in flight, we render a neutral skeleton
 * rather than redirecting (prevents the login-screen flicker on refresh).
 *
 * Optional `requireRole` enforces role-based access at the route level.
 */
import * as React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import type { UserRole } from "../lib/types";

interface Props {
  children: React.ReactNode;
  requireRole?: UserRole | UserRole[];
}

export function ProtectedRoute({ children, requireRole }: Props) {
  const { user } = useAuth();
  const location = useLocation();

  if (user === undefined) {
    // Checking session -- render a lightweight loading shell.
    return (
      <div
        data-testid="auth-checking"
        className="min-h-[60vh] flex items-center justify-center"
      >
        <div className="h-10 w-10 rounded-full border-4 border-surface-border border-t-brand animate-spin" />
      </div>
    );
  }

  if (user === null) {
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(location.pathname)}`}
        replace
      />
    );
  }

  if (requireRole) {
    const allowed = Array.isArray(requireRole) ? requireRole : [requireRole];
    if (!allowed.includes(user.role)) {
      return (
        <div className="mx-auto max-w-xl px-4 py-16 text-center">
          <h1 className="text-3xl font-black mb-3">Not allowed</h1>
          <p className="text-ink-muted">
            Your account doesn’t have permission to view this page.
          </p>
        </div>
      );
    }
  }

  return <>{children}</>;
}
